<script setup lang="ts">
const { t } = useI18n()
/* Shared, not local: bottom-pinned UI elsewhere waits while this card is up. */
const show = useCookieBannerVisible()

const STORAGE_KEYS = {
  accepted: 'cookie_banner_accepted',
  analyticsPref: 'analytics_tracking_enabled',
}

const checkShouldShow = () => {
  try {
    const accepted = localStorage.getItem(STORAGE_KEYS.accepted) === 'true'
    const analyticsDisabled = localStorage.getItem(STORAGE_KEYS.analyticsPref) === 'false'
    show.value = !accepted && !analyticsDisabled
  } catch {
    show.value = false
  }
}

const accept = () => {
  try {
    localStorage.setItem(STORAGE_KEYS.accepted, 'true')
    show.value = false
  } catch {
    show.value = false
  }
  announceCookieConsent(true)
}

/*
 * Declining stores the same preference the /cookies page toggle writes, so
 * the analytics plugin skips tracking and the banner stays hidden.
 */
const decline = () => {
  try {
    localStorage.setItem(STORAGE_KEYS.analyticsPref, 'false')
    localStorage.removeItem('visitor_id')
    sessionStorage.removeItem('session_id')
    sessionStorage.removeItem('session_start')
    show.value = false
  } catch {
    show.value = false
  }
  announceCookieConsent(false)
}

onMounted(() => {
  checkShouldShow()
})
</script>

<template>
  <transition
    enter-active-class="transform ease-out duration-300 transition"
    enter-from-class="translate-y-4 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      data-cookie-banner
      class="sofax fixed bottom-4 left-4 right-4 z-[60] sm:left-auto sm:max-w-sm"
      role="dialog"
      :aria-label="t('cookieBanner.title')"
    >
      <!--
        On a phone this is a compact bar, not the desktop card: it is pinned over
        the bottom of the screen, which is exactly where the pages underneath put
        their main button ("Create my CV", "Get 30 days"). The full-height card
        used to sit on top of that button in the first view.
      -->
      <div
        class="flex items-center gap-3 rounded-[0.625rem] border border-line bg-white p-3 shadow-lift sm:block sm:p-5 dark:border-gray-700 dark:bg-gray-800"
      >
        <div class="min-w-0 flex-1">
          <div class="hidden font-title text-lg font-bold text-ink sm:block dark:text-white">{{ t('cookieBanner.title') }}</div>
          <p class="!text-xs sm:mt-1 sm:!text-sm">
            {{ t('cookieBanner.text') }}
            <LocaleLink to="/cookies" class="link">{{ t('cookieBanner.details') }}</LocaleLink>
          </p>
        </div>
        <!-- Tighter buttons on the bar, so the sentence beside them keeps its width -->
        <div class="flex shrink-0 items-center gap-2 sm:mt-4 sm:flex-wrap">
          <SofaxBtn :label="t('cookieBanner.accept')" variant="accent" class="btn-sm !px-4 sm:!px-[1.875rem]" @click="accept" />
          <SofaxBtn :label="t('cookieBanner.decline')" variant="outline" class="btn-sm !px-4 sm:!px-[1.875rem]" @click="decline" />
        </div>
      </div>
    </div>
  </transition>
</template>
